// Colours are categorical data colours used to key each flag to its owning team in the list and
// legend. They are intentionally raw hex rather than design-system tokens: the tokens describe
// semantic UI roles (primary, success, danger) and carry no palette of mutually distinguishable
// category colours. That exemption is for this categorical palette only: surface, border, hover and
// progress colours are semantic roles and belong on tokens, so do not read this as licence to put
// chrome colours here or to add more hex alongside the components that draw these swatches.
// Exactly the six teams the backend defines — nothing is added client-side. Every flag has a team
// set in the backend, so a placeholder team would only add a legend and filter entry that can never
// match a flag. If an UNKNOWN team is ever wanted, it belongs in the backend enum and then becomes
// an ordinary seventh entry here.
export type FeatureFlagTeamId =
	| 'WORK_IN_PROGRESS'
	| 'FINANCE'
	| 'HOLD_YOUR_RESOURCES'
	| 'GEEK_SQUAD'
	| 'INTELLIGENCE'
	| 'ARCHITECTURE';

export interface FeatureFlagTeam {
	id: FeatureFlagTeamId;
	label: string;
	colour: string;
}

// Declaration order is load-bearing: it drives the legend order, the team filter's option order,
// and the primary sort key of the row list, so all three agree.
export const FEATURE_FLAG_TEAMS: readonly FeatureFlagTeam[] = [
	{id: 'WORK_IN_PROGRESS', label: 'Work In Progress', colour: '#0004db'},
	{id: 'FINANCE', label: 'Finance', colour: '#148f6a'},
	{
		id: 'HOLD_YOUR_RESOURCES',
		label: 'Hold Your Resources',
		colour: '#e57700',
	},
	{id: 'GEEK_SQUAD', label: 'Geek Squad', colour: '#e31672'},
	{id: 'INTELLIGENCE', label: 'Intelligence', colour: '#005d5d'},
	{id: 'ARCHITECTURE', label: 'Architecture', colour: '#a620a6'},
];

/**
 * Drawn for a flag whose team this map does not know — i.e. a team added to the backend but not
 * yet given a colour here. Such a flag keeps the backend's raw team name and renders in grey, so a
 * new backend team degrades gracefully instead of disappearing or crashing.
 */
export const UNMAPPED_TEAM_COLOUR = '#acacb9';

const TEAMS_BY_ID = new Map<string, FeatureFlagTeam>(FEATURE_FLAG_TEAMS.map(team => [team.id, team]));

/** The matching team id, or null when the backend sends a team this map does not know. */
export function resolveTeamId(team: string | null | undefined): FeatureFlagTeamId | null {
	if (!team || !TEAMS_BY_ID.has(team)) {
		return null;
	}
	return team as FeatureFlagTeamId;
}

export function getTeam(id: FeatureFlagTeamId): FeatureFlagTeam {
	// Safe by construction: FeatureFlagTeamId only admits ids present in the map above.
	return TEAMS_BY_ID.get(id) as FeatureFlagTeam;
}

/**
 * Position of a team in the declared order — the primary sort key of the flag list. An unmapped
 * team sorts after every known team.
 */
export function getTeamOrder(id: FeatureFlagTeamId | null): number {
	if (id == null) {
		return FEATURE_FLAG_TEAMS.length;
	}
	const index = FEATURE_FLAG_TEAMS.findIndex(team => team.id === id);
	return index === -1 ? FEATURE_FLAG_TEAMS.length : index;
}
